import React from 'react';
import { Chip, Stack, useTheme } from '@mui/material';

/*
Project Meta Components - Status Badge & Tag List
-------------------------------------------------
*/


//Muted grey used for the retired ("archived") state, independent of the project hue
const GREY = '#6e6e6e';


/*
Single-hue emphasis ladder built from the PROJECT's own brand color: a "live"
project is loud (solid fill), beta is an outline, concept is a dashed outline.
Archived is the exception — it falls back to grey so the desaturation itself
reads as "retired".
*/
const buildStatusStyles = (accent) => ({
  live: {
    label: 'Live',
    variant: 'filled',
    sx: { bgcolor: accent, color: '#000000' },
  },
  beta: {
    label: 'Beta',
    variant: 'outlined',
    sx: { color: accent, borderColor: accent },
  },
  archived: {
    label: 'Archived',
    light: { variant: 'outlined', sx: { color: GREY, borderColor: GREY } },
    dark: { variant: 'filled', sx: { bgcolor: GREY, color: '#ffffff' } },
  },
  concept: {
    label: 'Concept',
    variant: 'outlined',
    sx: { color: accent, borderColor: accent, borderStyle: 'dashed' },
  },
});


/* Status Badge - lifecycle pill, on-brand single-hue emphasis ladder */
const StatusBadge = ({ status, color, sx }) => {

  const theme = useTheme();
  //Live/Beta/Concept derive from the project's own brand color; archived stays grey
  const style = buildStatusStyles(color)[status];

  //Skip rendering when status is missing or unrecognized
  if (!style) return null;

  //Some statuses (archived) define mode-specific variants; fall back to the shared one
  const resolved = style[theme.palette.mode] || style;

  return (
    <Chip
      label={style.label}
      size="small"
      variant={resolved.variant}
      sx={{
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        height: 'auto',
        py: 0.35,
        '& .MuiChip-label': { px: 1.1 },
        ...resolved.sx,
        ...sx,
      }}
    />
  );
};


/* Tag List - outlined chips tinted with the project's accent color */
const TagList = ({ tags, color, sx }) => {

  //Skip rendering when there are no tags
  if (!tags || tags.length === 0) return null;

  return (
    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ ...sx }}>
      {tags.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          variant="outlined"
          sx={{
            color: color,
            borderColor: color,
            borderWidth: '1.5px',
            fontWeight: 600,
            fontSize: '0.95rem',
            height: 'auto',
            py: 0.9,
            '& .MuiChip-label': { px: 2 },
          }}
        />
      ))}
    </Stack>
  );
};


export { StatusBadge, TagList };
